import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../components/site/Page";
import sofia from "../assets/sofia-distans.png";
import hermods from "../assets/hermods.png";

const title = "Högstadium & gymnasium | Swedish International School";
const description =
  "Studera högstadiet via Sofia Distans och gymnasiet via Hermods i våra lokaler i Estepona, med handledare på plats, aktiviteter och gemenskap.";

export const Route = createFileRoute("/hogstadium-gymnasium")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: absoluteUrl("/hogstadium-gymnasium") },
      { property: "og:locale", content: "sv_SE" },
      ...ogImage(),
    ],
    links: seoLinks("/hogstadium-gymnasium"),
  }),
  component: HogstadiumGymnasium,
});

function HogstadiumGymnasium() {
  return (
    <>
      <PageHero
        eyebrow="Årskurs 7–9 och gymnasiet"
        title="Högstadium & gymnasium"
        intro="Elever i högstadiet och på gymnasiet studerar på distans via Sofia Distans respektive Hermods – med handledare, lokaler och gemenskap hos oss i Estepona."
      />

      <Prose>
        <h2>Högstadiet via Sofia Distans</h2>
        <img
          src={sofia}
          alt="Sofia Distans logotyp"
          width={140}
          height={140}
          loading="lazy"
          className="h-24 w-auto"
        />
        <p>
          För elever i högstadiet refererar vi till Sofia Distans, en distansskola baserad i
          Stockholm som på uppdrag av Skolverket erbjuder högstadiestudier för svenska elever
          utomlands. Eleverna kan studera i våra lokaler med våra handledare, men får sitt material
          och sin planering från Sofia Distans.
        </p>
        <p>
          Lärarna i Stockholm på Sofia Distans bedömer prov och inlämningar och sätter alla betyg.
          Två ansökningar görs: en till Sofia Distans och en till Swedish International School. Det
          innebär två skolavgifter – en till Sofia Distans, där man eventuellt kan få bidrag från
          Skolverket som starkt reducerar den avgiften. Därutöver betalar man en{" "}
          <Link to="/avgifter">avgift</Link> till vår skola för lokal, handledare, aktiviteter
          med mera.
        </p>
        <p>
          Elever som läser på distans arbetar självständigt men ingår ändå i en gemenskap i
          högstadieklassen och deltar i skolans olika aktiviteter och evenemang.
        </p>
        <p>
          Välkommen att gå högstadiet hos oss. <Link to="/ansokan">Ansök här.</Link>{" "}
          <a
            href="https://sofiadistans.stockholm.se/ansok-till-sofia-distans/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Läs mer om Sofia Distans och ansök till skolan här.
          </a>
        </p>

        <h2>Gymnasiet via Hermods</h2>
        <img
          src={hermods}
          alt="Hermods logotyp"
          width={200}
          height={52}
          loading="lazy"
          className="h-12 w-auto"
        />
        <p>
          Hermods Distansgymnasium erbjuder gymnasieutbildning på distans för ungdomar som vistas
          utomlands till följd av minst en förälders arbete. Hermods Distansgymnasium arbetar på
          uppdrag av Skolverket.
        </p>
        <p>På Hermods kan elever läsa följande studieförberedande gymnasieprogram:</p>
        <ul>
          <li>Naturvetenskapliga programmet</li>
          <li>Samhällsvetenskapliga programmet</li>
          <li>Ekonomiprogrammet</li>
          <li>Teknikprogrammet</li>
          <li>Humanistiska programmet</li>
        </ul>
        <p>
          Hermods har även över 150 enstaka kurser på gymnasial nivå. Eftersom Hermods
          Distansgymnasium inte har någon platsbegränsning får elever plats som är behöriga och
          tillhör målgruppen utlandssvenska gymnasieungdomar.
        </p>
        <p>
          Ansökan om plats i våra lokaler med handledare för Hermods distansstudier görs{" "}
          <Link to="/ansokan">här</Link>.{" "}
          <a href="https://hermods.se/gymnasium/anmalan/" target="_blank" rel="noopener noreferrer">
            Läs mer om Hermods och ansök till skolan här.
          </a>
        </p>

        <h2>Statsbidrag</h2>
        <p>
          För att finansiera studierna finns det möjlighet att ansöka hos Skolverket om statligt
          bidrag för svensk utbildning i utlandet. Vårdnadshavarens anställning ska uppfylla vissa
          villkor till grund för berättigande till statsbidrag. Det är således bara möjligt att söka
          statsbidrag för familjer där elev medföljer vårdnadshavare.
        </p>
        <p>
          Kriterier för statsbidrag och ansökningsblanketter finner du på{" "}
          <a
            href="https://www.skolverket.se/skolutveckling/statsbidrag/statsbidrag-for-distansundervisning-2023"
            target="_blank"
            rel="noopener noreferrer"
          >
            Skolverkets hemsida
          </a>
          . Där hittar ni också blanketterna som ska fyllas i.
        </p>

        <h2>Så ser studiedagen ut</h2>
        <p>
          Eleverna kommer till skolan på morgonen och arbetar med sina kurser i skolans lokaler under
          skoldagen. Handledaren hjälper till med veckoplanering, struktur, deadlines och att reda ut
          frågor i materialet, medan ämneslärarna på Sofia Distans respektive Hermods ansvarar för
          undervisning, bedömning och betyg. Dagen innehåller pauser och tid för rörelse.
        </p>
        <p>
          Distansstudier ställer krav på självständighet, men eleven är inte ensam. Att sitta i en
          grupp med andra elever, med vuxna på plats och fasta tider, gör det lättare att hålla ett
          jämnt tempo över terminen.
        </p>

        <h2>Kombinera med idrott</h2>
        <p>
          Många av våra äldre elever kombinerar studierna med{" "}
          <a className="text-primary link-quiet" href="/fotbollsakademi">fotbollsakademin</a> eller{" "}
          <a className="text-primary link-quiet" href="/padel-tennis">padel- och tennisprofilen</a>.
          Schemat läggs så att träning och studier går ihop, och eftersom kurserna läses på distans
          finns viss flexibilitet vid matcher och turneringar.
        </p>

        <h2>Bra att veta inför ansökan</h2>
        <ul>
          <li>Två ansökningar krävs: en till distansskolan och en till oss.</li>
          <li>Behörighetsfrågor och kursval hanteras av Sofia Distans respektive Hermods.</li>
          <li>Ta med tidigare betyg och studieplan så kan vi planera upplägget med er.</li>
          <li>Avgiften till oss avser lokal, handledning och aktiviteter – se{" "}
            <a className="text-primary link-quiet" href="/avgifter">avgifter</a>.</li>
        </ul>


      </Prose>

      <FaqBlock items={faqFor("hogstadium", "sv")} locale={"sv"} />

      <CtaBand />
      <div className="h-20" />
    </>
  );
}
